
import {Component, Prop, Watch} from "vue-property-decorator";
import {formRules} from "@/utils/formRules";
import {Getter} from "vuex-class";
import {defaultKeyPicture} from "@/utils/constants";
import {removeVuetifyFileInputBug} from "@/utils/misc";
import heic2any from "heic2any";
import {dataPicture} from "@/utils/pictureFonction";
import RelaysAutocomplete from "@/components/relays/RelaysAutocomplete.vue";
import { mixins } from "vue-class-component";
import DocumentMixin from "@/mixins/DocumentMixin.vue";
import MylobyAutocomplete from "@/components/generic/autocomplete.vue";

@Component({
    components: {
        RelaysAutocomplete,
        MylobyAutocomplete,
    },
})
export default class KeyCreate extends mixins(DocumentMixin) {
    @Prop({default: false})
    public show: boolean;

    @Prop({default: null})
    public buildingId: number | null;

    @Getter private isMobile: boolean;
    @Getter private currentUser: any;
    @Getter private envCompanyId: number|null;
    @Getter private canDoInEnvCompany: (right: string) => boolean;

    private removeVuetifyFileInputBug: (document: any) => void = removeVuetifyFileInputBug;
    private rules: object = formRules;
    private defaultKeyPicture: string = defaultKeyPicture;
    private showDialog: boolean = false;
    private loadingCreation: boolean = false;
    private newKey: any = {};
    private avatar: any = null;
    private uploadedImageSrc: any = null;
    private tags: any = [];
    private searchTags: string = null;
    private heicFile: any = null;
    private searchCategory: string = "";
    private categories: any[] = [];
    private isSearchingCategories: boolean = false;
    private displayBuildingSelection: boolean = true;

    get lang(): string {
        return this.$root.$i18n.locale;
    }

    get formattedCategoriesValues(): any[] {
        const lang: string = this.lang;
        return this.categories.map((category: any) => {
            return { text: category[lang], value: category.id };
        });
    }

    get hasCategory(): boolean {
        return !!this.newKey.categoryId;
    }

    get disabledSubmit(): boolean  {
        return !this.newKey.relayId || !this.newKey.name || !this.hasCategory;
    }

    @Watch("showDialog")
    public handlerShowDialog(newVal, oldVal) {
        if (newVal === false) {
            this.$emit("update:show", false);
            this.initNewKey();
        } else {
            this.$emit("update:show", false);
        }
    }

    @Watch("show")
    public handlerShow(newVal, oldVal) {
        if (newVal === true) {
            this.showDialog = true;
        }
    }

    private async mounted(): Promise<void> {
        this.initNewKey();
        this.getTags();
        this.getCategories();
    }

    private beforeUpdate(): void {
        this.removeVuetifyFileInputBug(document);
    }

    private initNewKey(): void {
        this.newKey = {
            name: "",
            description: "",
            relayId: null,
            categoryId: null,
            buildingId: null,
            picture: false,
            tags: [],
        };
    }

    private async fileChange(): Promise<void> {
        this.loadingCreation = true;
        this.heicFile = null;
        if (this.avatar) {
            if (this.avatar.type === "image/heif" || this.avatar.type === "image/heic" ) {
                const blob = this.avatar as Blob;
                this.heicFile = await heic2any({blob});
                const fr = new FileReader();
                fr.readAsDataURL(this.heicFile as Blob);
                fr.addEventListener("load", () => {
                    this.uploadedImageSrc = fr.result;
                });
            } else {
                const fr = new FileReader();
                fr.readAsDataURL(this.avatar);
                fr.addEventListener("load", () => {
                    this.uploadedImageSrc = fr.result;
                });
            }
        } else {
            this.uploadedImageSrc = null;
        }
        this.loadingCreation = false;
    }

    private changeRelay({ selectedRelay }): void {
        this.newKey.relayId = selectedRelay?.id;
        if (!!selectedRelay?.defaultCategoryId) {
            this.newKey.categoryId = selectedRelay.defaultCategoryId;
        }
    }

    private createKey(): void {
        this.loadingCreation = true;
        if (this.buildingId) {
          this.newKey.buildingId = this.buildingId;
        }
        this.$store.dispatch("keys/create", {
            key: {
              ...this.newKey,
              picturePath: !!this.avatar,
              tags: this.newKey.tags.map((t: any) => t?.id),
              companyId: this.envCompanyId,
            },
        }).then(async (res) => {
            if (this.avatar) {
                const data = await dataPicture(
                    res,
                    "key-picture" + Date.now(),
                    true,
                    this.currentUser,
                    this.heicFile,
                    this.avatar,
                );
                const saveImageRes = await this.$store.dispatch("files/uploadKeysPicture",
                  {formData: data.formData, keyId : res.id},
                );
                data.picture.name = saveImageRes.name;
                data.picture.picturePath = data.picture.picturePathFn(saveImageRes.name);
                await this.$store.dispatch("keys/addPicture", {
                    picture: data.picture,
                    keyId: res.id,
                });
            }
            if (res.status === "CREATED") {
                this.$store.commit("alerts/displaySuccess", {
                    icon: "icon-picto_cle-partagees",
                    msg: this.$i18n?.t("alerts.key.creationSuccess"),
                });
                this.close();
            } else {
                this.newKey = res;
            }
            this.loadingCreation = false;
        }).catch((error) => {
            this.$store.commit("alerts/displayHttpError", { error });
            this.close();
        });
    }

    private getTags(): void {
        this.$store
            .dispatch("tags/getAll", {
                query: {
                    search: this.searchTags,
                    lang: this.lang,
                },
            })
            .then((res) => {
                this.tags = res;
            });
    }

    private getCategories(): void {
        this.isSearchingCategories = true;
        this.$store.dispatch("categories/getAll")
            .then((res) => {
                this.categories = res.categories;
                this.isSearchingCategories = false;
            });
    }

    private hasRight(right: string): boolean {
      return this.canDoInEnvCompany(right);
    }

    private removeDuplicates(completeList, duplicates): any[] {
        if (!completeList || !duplicates) {
            return [];
        }
        return completeList.filter(
            (item: any) =>
                !duplicates.map((elem: any) => elem?.id)?.includes(item?.id),
        );
    }

    private remove(list, item): void {
        const pos = list.map((u) => u.id).indexOf(item.id);
        list.splice(pos, 1);
    }

    private close(): void {
        this.initNewKey();
        this.$emit("update-data");
        this.showDialog = false;
        this.loadingCreation = false;
        this.uploadedImageSrc = null;
        (this.$refs.relaysAutocomplete as HTMLFormElement)?.clear();
    }
}
